"use client"
import { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Stack,
  Button,
  Avatar,
  Paper,
  Container,
  Divider,
  Alert,
  AlertTitle,
  useTheme,
  Zoom,
  Tooltip,
} from "@mui/material"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"
import { useWebSocket } from "../context/WebSocketContext"
import PaymentIcon from "@mui/icons-material/Payment"
import DiscordIcon from "@mui/icons-material/Chat"
import AddIcon from "@mui/icons-material/Add"
import config from "../config"
import TrackingIcon from "@mui/icons-material/MyLocation"

const Profile = () => {
  const theme = useTheme()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { discordRequired } = useWebSocket()
  const DISCORD_INVITE_LINK = config.DISCORD_INVITE_LINK
  const PRICING_LINK = config.PRICING_LINK
  const [clickedRole, setClickedRole] = useState(null)
  const [addRoleHovered, setAddRoleHovered] = useState(false)

  const roles = user?.roles ?? []

  useEffect(() => {
    if (!user) {
      navigate("/")
      return
    }
  }, [user, navigate])

  // Handle external navigation
  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  // Handle role chip click
  const handleRoleClick = (roleId) => {
    setClickedRole(roleId)
    setTimeout(() => setClickedRole(null), 800) // Reset after ripple effect
  }

  // Handle add role click
  const handleAddRoleClick = () => {
    handleExternalNavigation(PRICING_LINK, "chat-tier Store")
  }

  if (!user) {
    return null
  }

  // Helper to format Discord avatar URL
  function getDiscordAvatarUrl(user) {
    if (!user || !user.id) return ""
    if (!user.avatar) {
      // Default avatar (use Discord CDN default)
      const defaultId = Number.parseInt(user.id) % 5
      return `https://cdn.discordapp.com/embed/avatars/${defaultId}.png`
    }
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
  }

  // Get role color for dot
  const getRoleDotColor = (role) => {
    if (!role.color) return "#ffffff"
    return `#${role.color.replace(/^#/, "")}`
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 0,
          overflow: "hidden",
          background: "rgba(13, 15, 25, 0.7)",
          border: "1px solid rgba(0, 229, 255, 0.1)",
          borderRadius: 2,
          position: "relative",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
        }}
      >
        {/* Decorative header */}
        <Box
          sx={{
            height: "80px",
            background: "linear-gradient(90deg, rgba(0,229,255,0.1) 0%, rgba(0,0,0,0) 100%)",
            borderBottom: "1px solid rgba(0, 229, 255, 0.1)",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            px: 4,
          }}
        >
          {/* Circuit pattern overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='%2300e5ff' strokeWidth='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E\")",
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: "bold",
              textShadow: "0 0 10px rgba(0, 229, 255, 0.3)",
              zIndex: 1,
            }}
          >
            Profile
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4 }}>
            <Avatar
              src={getDiscordAvatarUrl(user)}
              alt={user.global_name || user.username}
              sx={{
                width: 80,
                height: 80,
                border: "2px solid rgba(0, 229, 255, 0.5)",
                boxShadow: "0 0 10px rgba(0, 229, 255, 0.3)",
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {user.global_name || user.username}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                @{user.username}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: "rgba(255, 255, 255, 0.1)" }} />

          <Stack spacing={3}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontWeight: "medium",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Roles
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                {roles.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No roles found.
                  </Typography>
                ) : (
                  roles.map((role) => {
                    const isHighlighted = role.id === clickedRole
                    const dotColor = getRoleDotColor(role)

                    return (
                      <Tooltip
                        key={role.id}
                        title="Role from Discord"
                        placement="top"
                        TransitionComponent={Zoom}
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "rgba(13, 15, 25, 0.9)",
                              border: "1px solid rgba(0, 229, 255, 0.3)",
                              boxShadow: "0 0 15px rgba(0, 229, 255, 0.3)",
                              color: "white",
                              fontSize: "0.75rem",
                              padding: "6px 10px",
                              borderRadius: "4px",
                              "& .MuiTooltip-arrow": {
                                color: "rgba(13, 15, 25, 0.9)",
                                "&::before": {
                                  border: "1px solid rgba(0, 229, 255, 0.3)",
                                  backgroundColor: "rgba(13, 15, 25, 0.9)",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <Button
                          onClick={() => handleRoleClick(role.id)}
                          sx={{
                            bgcolor: isHighlighted ? "rgba(70, 70, 70, 0.9)" : "rgba(50, 50, 50, 0.8)",
                            color: "white",
                            textTransform: "none",
                            borderRadius: "16px",
                            py: 0.5,
                            px: 1.5,
                            minWidth: 0,
                            height: "28px",
                            fontSize: "0.85rem",
                            fontWeight: "normal",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(70, 70, 70, 0.9)",
                            },
                            "&:active": {
                              bgcolor: "rgba(80, 80, 80, 0.9)",
                            },
                            "& .MuiTouchRipple-child": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: dotColor,
                              flexShrink: 0,
                            }}
                          />
                          {role.name ? role.name : `Unknown_${role.id}`}
                        </Button>
                      </Tooltip>
                    )
                  })
                )}

                {/* Add Role Button */}
                <Tooltip
                  title="Get more roles"
                  placement="top"
                  TransitionComponent={Zoom}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "rgba(13, 15, 25, 0.9)",
                        border: "1px solid rgba(0, 229, 255, 0.3)",
                        boxShadow: "0 0 15px rgba(0, 229, 255, 0.3)",
                        color: "white",
                        fontSize: "0.75rem",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        "& .MuiTooltip-arrow": {
                          color: "rgba(13, 15, 25, 0.9)",
                          "&::before": {
                            border: "1px solid rgba(0, 229, 255, 0.3)",
                            backgroundColor: "rgba(13, 15, 25, 0.9)",
                          },
                        },
                      },
                    },
                  }}
                >
                  <Button
                    onClick={handleAddRoleClick}
                    onMouseEnter={() => setAddRoleHovered(true)}
                    onMouseLeave={() => setAddRoleHovered(false)}
                    sx={{
                      bgcolor: addRoleHovered ? "rgba(70, 70, 70, 0.9)" : "rgba(50, 50, 50, 0.8)",
                      color: "white",
                      minWidth: "28px",
                      width: "28px",
                      height: "28px",
                      p: 0,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "rgba(70, 70, 70, 0.9)",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/in-range-players")}
                startIcon={<TrackingIcon />}
                sx={{
                  background: "linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)",
                  boxShadow: "0 0 10px rgba(76, 175, 80, 0.5)",
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(76, 175, 80, 0.8)",
                  },
                }}
              >
                Player Tracker
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => handleExternalNavigation("https://chat-tier.com/my/orders", "chat-tier Profile")}
                startIcon={<PaymentIcon />}
                sx={{
                  background: "linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)",
                  boxShadow: "0 0 10px rgba(0, 229, 255, 0.5)",
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(0, 229, 255, 0.8)",
                  },
                }}
              >
                Manage Subscription
              </Button>
            </Stack>

            {discordRequired && (
              <Alert
                severity="error"
                sx={{
                  mt: 3,
                  bgcolor: "rgba(211, 47, 47, 0.1)",
                  border: "1px solid rgba(211, 47, 47, 0.3)",
                  borderRadius: 2,
                }}
              >
                <AlertTitle>Discord Required</AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  You are not in our Discord server
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleExternalNavigation(DISCORD_INVITE_LINK, "Discord Server")}
                  startIcon={<DiscordIcon />}
                  size="small"
                >
                  Join Discord Server
                </Button>
              </Alert>
            )}
          </Stack>
        </Box>
      </Paper>
    </Container>
  )
}

export default Profile
